import { bindActionCreators } from 'redux'
import { showSpinner, clearUserData, showModal } from '../actions/index'
import store from '../services/store'
import Api from '../services/api'
import * as Socket from '../services/socket'

const bound = bindActionCreators({ showSpinner, clearUserData, showModal }, store.dispatch)

const LOGOUT_SIGNAL_KEY = 'logged-out-at'

let lastSeenLogoutAt = null

const readSignal = () => {
  try {
    return window.localStorage.getItem(LOGOUT_SIGNAL_KEY)
  } catch (err) {
    return null
  }
}

const writeSignal = (value) => {
  try {
    window.localStorage.setItem(LOGOUT_SIGNAL_KEY, value)
  } catch (err) {
  }
  lastSeenLogoutAt = value
}

const SESSION_CHECK_MS = 60 * 1000

let checkingSession = false

const checkSessionWithServer = async () => {
  if (checkingSession || !store.getState().user.loggedIn) {
    return
  }
  checkingSession = true
  try {
    let res = await Api.getUser(null, { scene: 'sign in' })
    if (res === 401 || res === 403) {
      console.log('[logout] the session is gone, signing this tab out')
      logOutModule.init({ fromOtherTab: true })
      window.location.hash = '#/main/upcoming'
    }
  } catch (err) {
  }
  checkingSession = false
}

const logOutModule = {
  watchInterval: null,
  sessionInterval: null,

  init: ({ force = false, leaveAuction = false, fromOtherTab = false } = {}) => {
    if (force) {
      bound.showModal({
        title: 'Unauthorized',
        message: 'Looks like you are not authorized, please try to sign in again'
      })
    }
    let signedOutAt = fromOtherTab ? (lastSeenLogoutAt || String(Date.now())) : String(Date.now())
    bound.showSpinner(false)
    bound.clearUserData()
    window.localStorage.clear()
    window.sessionStorage.clear()
    Socket.disconnectAll()
    writeSignal(signedOutAt)
    if (!force && !fromOtherTab) {
      Api.userLogout().then(() => {
        if (leaveAuction) {
          window.location.reload()
        }
      })
    }
  },

  listenForOtherTabs: () => {
    lastSeenLogoutAt = readSignal()
    console.log('[logout] watching other tabs, last sign-out seen:', lastSeenLogoutAt)

    const signOutWithOtherTab = (why) => {
      let signal = readSignal()
      // nothing new: no sign-out recorded, or one this tab already went with
      if (!signal || signal === lastSeenLogoutAt) {
        return
      }
      lastSeenLogoutAt = signal
      if (!store.getState().user.loggedIn) {
        return
      }
      console.log(`[logout] signed out with another tab (${why})`)
      logOutModule.init({ fromOtherTab: true })
      window.location.hash = '#/main/upcoming'
    }
    window.addEventListener('storage', (event) => {
      // `clear()` fires an event with no key of its own, every other key is
      // some other tab going about its business
      if (event.key && event.key !== LOGOUT_SIGNAL_KEY) {
        return
      }
      signOutWithOtherTab('storage event')
    })
    window.addEventListener('focus', () => signOutWithOtherTab('focus'))
    window.addEventListener('visibilitychange', () => {
      if (!document.hidden) {
        signOutWithOtherTab('visible')
      }
    })
    if (!logOutModule.watchInterval) {
      logOutModule.watchInterval = setInterval(() => signOutWithOtherTab('check'), 1000)
    }

    // ...and none of the above travels between tabs that do not share their
    // storage - a webapp embedded in the website is given its own, and so is a
    // tab on another address. The session itself is the one thing they do
    // share, so it is asked after: signing out ends it for the whole browser,
    // and a tab whose session is gone signs itself out.
    window.addEventListener('focus', checkSessionWithServer)
    window.addEventListener('visibilitychange', () => {
      if (!document.hidden) {
        checkSessionWithServer()
      }
    })
    if (!logOutModule.sessionInterval) {
      logOutModule.sessionInterval = setInterval(checkSessionWithServer, SESSION_CHECK_MS)
    }
  }
}

export default logOutModule
